import { MainLayout } from '@/components/layout/MainLayout';
import { JobForm } from '@/components/jobs/JobForm';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getJobById, updateJob } from '@/lib/api/jobs';
import { jobPath } from '@/lib/utils/navPaths';

export const JobEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [initialData, setInitialData] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    loadJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadJob = async () => {
    setLoadError('');
    try {
      setLoading(true);
      if (!id) return;

      const jobData = await getJobById(id);

      setInitialData({
        title: jobData.title,
        clientId: jobData.client_id,
        siteAddress: jobData.site_address,
        startDate: jobData.start_date,
        estimatedEndDate: jobData.end_date,
        projectManagerId: jobData.project_manager_id,
        pmSource: jobData.pm_source,
        projectManagerCustom: jobData.project_manager_custom,
        budget: jobData.budget,
        description: jobData.description,
        status: jobData.status,
      });
    } catch (error) {
      console.error('Failed to load job:', error);
      setLoadError('Failed to load job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData: Record<string, unknown>) => {
    if (!id) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await updateJob(id, formData);
      navigate(jobPath(formData.client_id as string | undefined, id));
    } catch (error) {
      console.error('Failed to update job:', error);
      setSubmitError('Failed to update job. Please check your inputs and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto space-y-4 p-6">
          <div className="h-9 bg-gray-100 rounded-xl animate-pulse w-40" />
          <div className="h-80 bg-gray-100 rounded-xl animate-pulse" />
        </div>
      </MainLayout>
    );
  }

  if (loadError) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto flex flex-col items-center justify-center h-64 gap-3">
          <span className="material-symbols-outlined text-gray-300 text-5xl">error</span>
          <p className="text-gray-500 font-medium">{loadError}</p>
          <button onClick={loadJob} className="text-sm font-semibold text-primary border border-primary/30 rounded-lg px-4 py-2 hover:bg-primary/5">
            Retry
          </button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-black leading-tight tracking-[-0.033em] text-gray-900">
            Edit Job
          </h1>
          <p className="text-gray-600 mt-1">
            Update job details
          </p>
        </div>

        {submitError && (
          <div className="mb-4 flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <span className="material-symbols-outlined text-base flex-shrink-0">error</span>
            <span className="flex-1">{submitError}</span>
            <button onClick={() => setSubmitError('')} className="text-red-400 hover:text-red-600 flex-shrink-0">
              <span className="material-symbols-outlined text-sm">close</span>
            </button>
          </div>
        )}

        {submitting && (
          <div className="mb-4 flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
            <span>Saving changes…</span>
          </div>
        )}

        <JobForm onSubmit={handleSubmit} initialData={initialData ?? undefined} />
      </div>
    </MainLayout>
  );
};
